import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import SheetDetailModal from './modals/SheetDetailModal';
import SendToProductionModal from './modals/SendToProductionModal';
import * as MdIcons from 'react-icons/md'
import Button from '../ui/Button';

const STATUS_COLORS = {
    Saved:           'bg-blue-100 text-blue-700',
    'In Production': 'bg-amber-100 text-amber-700',
    Completed:       'bg-emerald-100 text-emerald-700',
};

const STATUS_OPTIONS = ['', 'Saved', 'In Production', 'Completed'];

const ProcessSheetList = ({ onCreateNew, onEdit }) => {
    const [sheets, setSheets]         = useState([]);
    const [loading, setLoading]       = useState(false);
    const [page, setPage]             = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatus]   = useState('');
    const [search, setSearch]         = useState('');
    const [searchInput, setSearchInput] = useState(''); // uncontrolled until submit
    const [detailId, setDetailId]     = useState(null);
    const [productionTarget, setProductionTarget] = useState(null);

    const fetchSheets = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            if (statusFilter) params.set('status', statusFilter);
            if (search)       params.set('search', search);
            const res = await api.get(`/process-sheets?${params}`);
            setSheets(res.data.sheets || []);
            setTotalPages(res.data.totalPages || 1);
        } catch {
            toast.error('Failed to load process sheets');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, search]);

    useEffect(() => { fetchSheets(); }, [fetchSheets]);

    const fmt = (dateStr) => dateStr
        ? new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })
        : '—';

    const gradeString = (inputs) => {
        if (!inputs) return '—';
        const { gritCode, bondType, concentration } = inputs;
        if (gritCode && bondType) return `${gritCode}-${bondType}-${concentration}`;
        return inputs.grade || '—'; // fallback for old records
    };

    return (
        <div>
            <SheetDetailModal
                sheetId={detailId}
                isOpen={!!detailId}
                onClose={() => setDetailId(null)}
            />

            <SendToProductionModal
                sheet={productionTarget}
                isOpen={!!productionTarget}
                onClose={() => setProductionTarget(null)}
                onSuccess={() => {
                    setProductionTarget(null);
                    fetchSheets();
                }}
            />

            {/* ── Toolbar ───────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h1 className="text-xl font-bold text-gray-800">Process Sheets</h1>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Search by client or batch */}
                    <form
                        onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setPage(1); }}
                        className="flex gap-1"
                    >
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Client or batch…"
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-40"
                        />
                        <button type="submit"
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                            Search
                        </button>
                        {search && (
                            <button type="button"
                                onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
                                className="px-2 py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors"
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </form>

                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s || 'All Statuses'}</option>
                        ))}
                    </select>
                    <button onClick={onCreateNew}
                        className="px-4 py-2 bg-gray-900 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors">
                        + New Sheet
                    </button>
                </div>
            </div>

            {/* ── Table ─────────────────────────────────────────────── */}
            <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sheet No.</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Client</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Grade</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                                {/* <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created By</th> */}
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-12 text-gray-400">Loading…</td>
                                </tr>
                            )}
                            {!loading && sheets.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-12 text-gray-400">
                                        No process sheets found.
                                        <button onClick={onCreateNew} className="ml-2 text-blue-500 hover:underline">
                                            Create one →
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {!loading && sheets.map((sheet) => {
                                const isSaved = sheet.status === 'Saved';
                                return (
                                    <tr key={sheet._id} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 font-mono text-xs font-bold text-gray-700">
                                            {sheet.sheetNumber}
                                        </td>
                                        <td className="px-4 py-3 text-gray-800">{sheet.clientName || '—'}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                            {gradeString(sheet.inputs)}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600 tracking-widest">
                                            {sheet.inputs?.batchNumber || '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[sheet.status] || 'bg-gray-100 text-gray-600'}`}>
                                                {sheet.status}
                                            </span>
                                        </td>
                                        {/* <td className="px-4 py-3 text-gray-500 text-xs">{sheet.createdBy || '—'}</td> */}
                                        <td className="px-4 py-3 text-gray-500 text-xs">{fmt(sheet.createdAt)}</td>

                                        {/* ── Actions ──────────────────────────── */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end">
                                                <Button variant="outline" size="sm"
                                                    title="View"
                                                     onClick={() => setDetailId(sheet._id)}
                                                >
                                                    <MdIcons.MdOutlineRemoveRedEye className="h-3 w-3" />
                                                    <span className="sr-only">View</span>
                                                </Button>
                                                {isSaved && (
                                                    <Button onClick={() => onEdit(sheet)}
                                                    size="sm"
                                                    title="Edit"
                                                >
                                                    <MdIcons.MdEditSquare className="h-3 w-3" />
                                                    <span className="sr-only">Edit</span>
                                                </Button>
                                                )}
                                                {isSaved && (
                                                    <Button onClick={() => setProductionTarget(sheet)}
                                                    size="sm" variant="green"
                                                    title="Push to Production"
                                                >
                                                    <MdIcons.MdRocketLaunch className="h-3 w-3" />
                                                    <span className="sr-only">Production</span>
                                                </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                            ← Prev
                        </button>
                        <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProcessSheetList;